/**
 * Shared hearing constants — single source of truth for all hearing-related
 * dropdowns and status display across the hearing system.
 */

export const HEARING_TYPES: { label: string; value: string }[] = [
  { label: 'Trial',          value: 'trial'            },
  { label: 'CMC',            value: 'case_management'  },
  // PTR (civil Pre-Trial Review) and PTPH (Crown Court Plea and Trial Preparation Hearing) are
  // different hearings. They shared the `pre_trial_review` value until 2026-10-14, so choosing
  // PTPH silently recorded — and then displayed — a PTR.
  { label: 'PTR',            value: 'pre_trial_review'  },
  { label: 'PTPH',           value: 'ptph'              },
  { label: 'First listing',  value: 'first_listing'    },
  { label: 'Application',    value: 'interlocutory'    },
  { label: 'Sentencing',     value: 'sentencing'       },
  { label: 'Appeal',         value: 'appeal'           },
  { label: 'Mention',        value: 'mention'          },
  { label: 'Bail',           value: 'bail'             },
  { label: 'Costs',          value: 'costs'            },
  { label: 'Mediation',      value: 'mediation'        },
  { label: 'Other',          value: 'other'            },
];

// ─── Legal outcome types ────────────────────────────────────────────────────

export interface LegalOutcomeType {
  value: string;
  label: string;
  needsNextDate?: boolean;
  needsJudgment?: boolean;
  isFinal?: boolean;
  hasOrders?: boolean;
  suggestMatterClose?: boolean;
  /** HearingStatus to set after this outcome */
  statusAfter?: string;
}

export const LEGAL_OUTCOME_TYPES: LegalOutcomeType[] = [
  // Continued proceedings
  { value: 'adjourned',           label: 'Adjourned',                needsNextDate: true,  statusAfter: 'adjourned'                                      },
  { value: 'trial_continued',     label: 'Trial Continued',          needsNextDate: true,  statusAfter: 'adjourned'                                      },
  // Interlocutory / interim
  { value: 'directions_given',    label: 'Directions Given',         hasOrders: true, needsNextDate: true, statusAfter: 'awaiting_compliance'             },
  { value: 'interim_order',       label: 'Interim Order Made',       hasOrders: true,      statusAfter: 'awaiting_compliance'                            },
  { value: 'application_granted', label: 'Application Granted',      hasOrders: true,      statusAfter: 'completed'                                      },
  { value: 'application_refused', label: 'Application Refused',      hasOrders: true,      statusAfter: 'completed'                                      },
  // Judgment
  { value: 'judgment_reserved',   label: 'Judgment Reserved',        needsJudgment: true,  statusAfter: 'order_reserved'                                 },
  { value: 'final_judgment',      label: 'Final Judgment Delivered', isFinal: true, hasOrders: true, statusAfter: 'completed'    },
  { value: 'consent_order',       label: 'Consent Order',            isFinal: true, hasOrders: true, statusAfter: 'completed'                            },
  // Disposal — civil
  { value: 'settlement_reached',  label: 'Settlement Reached',       isFinal: true, suggestMatterClose: true, statusAfter: 'disposed' },
  { value: 'matter_withdrawn',    label: 'Matter Withdrawn',         isFinal: true, suggestMatterClose: true, statusAfter: 'disposed' },
  { value: 'strike_out',          label: 'Strike Out',               isFinal: true, suggestMatterClose: true, statusAfter: 'disposed' },
  // Criminal-specific
  { value: 'acquittal',           label: 'Acquittal / Not Guilty',   isFinal: true, suggestMatterClose: true, statusAfter: 'disposed' },
  { value: 'conviction',          label: 'Conviction / Guilty Plea', isFinal: true, suggestMatterClose: true, statusAfter: 'disposed'                                  },
  { value: 'sentence_passed',     label: 'Sentence Passed',          isFinal: true, hasOrders: true, statusAfter: 'disposed'                                           },
  { value: 'bail_granted',        label: 'Bail Granted',             hasOrders: true, statusAfter: 'completed'                                                         },
  { value: 'bail_refused',        label: 'Bail Refused / Remanded',  statusAfter: 'completed'                                                                          },
];

// ─── Hearing status display config ─────────────────────────────────────────

export type HearingStatusKey =
  | 'scheduled' | 'confirmed' | 'in_progress'
  | 'awaiting_appearance' | 'awaiting_compliance'
  | 'adjourned' | 'order_reserved' | 'judgment_pending'
  | 'completed' | 'vacated' | 'no_show' | 'disposed';

export interface StatusConfig {
  label: string;
  cls: string;       // Tailwind classes for badge pill
  dot: string;       // Tailwind color for status dot
  nextAction?: string;
  isActive: boolean;
}

export const HEARING_STATUS_CONFIG: Record<HearingStatusKey, StatusConfig> = {
  scheduled:           { label: 'Scheduled',           cls: 'bg-slate-100 text-slate-700 dark:text-slate-300',    dot: 'bg-slate-400',   nextAction: 'Confirm listing',         isActive: true  },
  confirmed:           { label: 'Confirmed',           cls: 'bg-emerald-100 text-emerald-700 dark:text-emerald-300',dot: 'bg-emerald-500', nextAction: 'Review hearing brief',    isActive: true  },
  in_progress:         { label: 'In Progress',         cls: 'bg-brand-100 text-brand-700 dark:text-brand-300',    dot: 'bg-brand-500',   nextAction: 'Record outcome',          isActive: true  },
  awaiting_appearance: { label: 'Awaiting Appearance', cls: 'bg-amber-100 text-amber-700 dark:text-amber-300',    dot: 'bg-amber-500',   nextAction: 'Confirm attendance',      isActive: true  },
  awaiting_compliance: { label: 'Awaiting Compliance', cls: 'bg-amber-100 text-amber-700 dark:text-amber-300',    dot: 'bg-amber-500',   nextAction: 'Chase compliance order',  isActive: true  },
  adjourned:           { label: 'Adjourned',           cls: 'bg-amber-100 text-amber-700 dark:text-amber-300',    dot: 'bg-amber-500',   nextAction: 'Schedule next hearing',   isActive: false },
  order_reserved:      { label: 'Order Reserved',      cls: 'bg-violet-100 text-violet-700 dark:text-violet-300',  dot: 'bg-violet-500',  nextAction: 'Await judgment',          isActive: false },
  judgment_pending:    { label: 'Judgment Pending',    cls: 'bg-violet-100 text-violet-700 dark:text-violet-300',  dot: 'bg-violet-500',  nextAction: 'Monitor for judgment',    isActive: false },
  completed:           { label: 'Completed',           cls: 'bg-emerald-100 text-emerald-700 dark:text-emerald-300',dot: 'bg-emerald-500',                                         isActive: false },
  vacated:             { label: 'Vacated',             cls: 'bg-red-100 text-red-700 dark:text-red-300',        dot: 'bg-red-500',                                             isActive: false },
  no_show:             { label: 'No Show',             cls: 'bg-red-100 text-red-700 dark:text-red-300',        dot: 'bg-red-500',     nextAction: 'Relist hearing',          isActive: false },
  disposed:            { label: 'Disposed',            cls: 'bg-slate-100 text-slate-700 dark:text-slate-300',    dot: 'bg-slate-400',                                           isActive: false },
};

export function getStatusConfig(status: string): StatusConfig {
  return HEARING_STATUS_CONFIG[status as HearingStatusKey] ?? {
    label: status.replace(/_/g, ' '),
    cls: 'bg-slate-100 text-slate-700 dark:text-slate-300',
    dot: 'bg-slate-400',
    isActive: true,
  };
}

// ─── Hearing durations ──────────────────────────────────────────────────────

export const HEARING_DURATIONS: { label: string; minutes: number }[] = [
  { label: '30 min',   minutes: 30   },
  { label: '1 hour',   minutes: 60   },
  { label: '2 hours',  minutes: 120  },
  { label: '3 hours',  minutes: 180  },
  { label: '4 hours',  minutes: 240  },
  { label: 'Full day', minutes: 480  },
  { label: '2 days',   minutes: 960  },
  { label: '3 days',   minutes: 1440 },
  { label: '5 days',   minutes: 2400 },
];

// ─── Costs positions ────────────────────────────────────────────────────────

export const COSTS_OPTIONS = [
  'Costs to claimant / applicant',
  'Costs to defendant / respondent',
  'Costs in the case',
  'Costs reserved',
  'No order as to costs',
  'Summary assessment',
  'Detailed assessment ordered',
] as const;

// ─── Outcome validation — ONE rule, read by every screen that records an outcome ─────────
//
// Rule #14: the hearing list, the barrister's court list, the hearing detail page and the
// Record Past Hearing form all capture the same two facts, so they ask for them on the same
// terms and refuse them with the same words. Before this, each screen expressed its refusal
// by disabling its save button and saying nothing.

/** `outcome` is capped at 5 000 by apps/api/src/modules/hearings/hearings.schema.ts. */
export const OUTCOME_NOTES_MAX = 5000;

/** Shortest summary that tells the next reader what actually happened. */
export const OUTCOME_SUMMARY_MIN = 10;

/**
 * The message for a missing or useless outcome summary — `null` when it is fine.
 * `isMeaningful` comes from `isMeaningfulTitle`: it rejects "aaaaaaaaaa" and punctuation.
 */
export function outcomeSummaryError(
  text: string,
  isMeaningful: (v: string) => boolean,
  submitted: boolean,
): string | null {
  const t = text.trim();
  if (!t) return submitted ? 'Record what happened at the hearing.' : null;
  if (t.length < OUTCOME_SUMMARY_MIN) {
    return `Give at least ${OUTCOME_SUMMARY_MIN} characters — this is what the next person to open the matter will read.`;
  }
  if (!isMeaningful(t)) return 'Describe the outcome in words — a repeated character is not a summary.';
  return null;
}

/** The message for a missing result — `null` when one has been chosen. */
export function outcomeTypeErrorFor(outcomeType: string, submitted: boolean): string | null {
  if (outcomeType) return null;
  return submitted ? 'Choose what the hearing decided.' : null;
}
